import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Post } from '@/lib/api';
import { softDeleteMany } from '@/lib/masterDataValidation';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { RolesSection } from '@/components/settings/roles/RolesSection';
import { DepartmentsSection } from '@/components/settings/roles/DepartmentsSection';
import { IndustriesSection } from '@/components/settings/roles/IndustriesSection';
import { WorkCategoriesDialog } from '@/components/settings/roles/WorkCategoriesDialog';
import { toast } from 'sonner';

export default function RolesSettings() {
  const qc = useQueryClient();
  const { data: roles = [], isLoading: rolesLoading, isError: rolesError } = useQuery({
    queryKey: ['v1', 'roles'],
    queryFn: () => v1Get('/master/roles'),
  });
  const { data: depts = [], isLoading: deptsLoading, isError: deptsError } = useQuery({
    queryKey: ['v1', 'departments'],
    queryFn: () => v1Get('/master/departments'),
  });
  const { data: pipelines = [] } = useQuery({
    queryKey: ['v1', 'pipelines'],
    queryFn: () => v1Get('/pipelines'),
  });
  const { data: industries = [], isLoading: industriesLoading, isError: industriesError } = useQuery({
    queryKey: ['v1', 'industries'],
    queryFn: () => v1Get('/master/industries'),
  });

  const roleClearRef = useRef(null);
  const deptSectionRef = useRef(null);

  const [selectedIndustryId, setSelectedIndustryId] = useState(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [catOpen, setCatOpen] = useState(null);
  const [catInitKey, setCatInitKey] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [industryOpen, setIndustryOpen] = useState(false);
  const [industryForm, setIndustryForm] = useState({ code: '', name: '' });
  const [industrySaving, setIndustrySaving] = useState(false);

  const registerRoleClearSelection = useCallback((fn) => {
    roleClearRef.current = fn;
  }, []);

  const clearSelections = useCallback(() => {
    roleClearRef.current?.();
    deptSectionRef.current?.clearSelection();
  }, []);

  // Drop stale drill-down selection if the parent was deleted or disappeared from the list.
  useEffect(() => {
    if (selectedIndustryId && !(industries || []).some((i) => i.id === selectedIndustryId)) {
      setSelectedIndustryId(null);
      setSelectedDepartmentId(null);
      return;
    }
    if (selectedDepartmentId && !(depts || []).some((d) => d.id === selectedDepartmentId)) {
      setSelectedDepartmentId(null);
    }
  }, [industries, depts, selectedIndustryId, selectedDepartmentId]);

  const selectedIndustry = useMemo(
    () => (industries || []).find((i) => i.id === selectedIndustryId) || null,
    [industries, selectedIndustryId],
  );

  const selectedDepartment = useMemo(
    () => (depts || []).find((d) => d.id === selectedDepartmentId) || null,
    [depts, selectedDepartmentId],
  );

  const deptCountByIndustry = useMemo(() => {
    const counts = {};
    for (const d of depts || []) {
      if (!d.industry_id) continue;
      counts[d.industry_id] = (counts[d.industry_id] || 0) + 1;
    }
    return counts;
  }, [depts]);

  const roleCountByDept = useMemo(() => {
    const counts = {};
    for (const r of roles || []) {
      if (!r.department_id) continue;
      counts[r.department_id] = (counts[r.department_id] || 0) + 1;
    }
    return counts;
  }, [roles]);

  const roleCountByIndustry = useMemo(() => {
    const deptIndustry = {};
    for (const d of depts || []) {
      if (!d.id || !d.industry_id) continue;
      deptIndustry[d.id] = d.industry_id;
    }
    const counts = {};
    for (const r of roles || []) {
      const industryId = deptIndustry[r.department_id];
      if (!industryId) continue;
      counts[industryId] = (counts[industryId] || 0) + 1;
    }
    return counts;
  }, [depts, roles]);

  const filteredDepts = useMemo(() => {
    if (!selectedIndustryId) return [];
    return (depts || []).filter((d) => d.industry_id === selectedIndustryId);
  }, [depts, selectedIndustryId]);

  const filteredRoles = useMemo(() => {
    if (!selectedDepartmentId) return [];
    return (roles || []).filter((r) => r.department_id === selectedDepartmentId);
  }, [roles, selectedDepartmentId]);

  const industryScopedDepts = useMemo(() => {
    if (!selectedIndustryId) return depts || [];
    return (depts || []).filter((d) => d.industry_id === selectedIndustryId);
  }, [depts, selectedIndustryId]);

  const handleSelectIndustry = useCallback((industry) => {
    clearSelections();
    setSelectedIndustryId(industry.id);
    setSelectedDepartmentId(null);
  }, [clearSelections]);

  const handleSelectDepartment = useCallback((department) => {
    clearSelections();
    setSelectedDepartmentId(department.id);
  }, [clearSelections]);

  const goToIndustries = useCallback(() => {
    clearSelections();
    setSelectedIndustryId(null);
    setSelectedDepartmentId(null);
  }, [clearSelections]);

  const goToDepartments = useCallback(() => {
    clearSelections();
    setSelectedDepartmentId(null);
  }, [clearSelections]);

  const handleManageCategories = useCallback((d) => {
    setCatInitKey((k) => k + 1);
    setCatOpen(d);
  }, []);

  const confirmDelete = async () => {
    if (!deleteConfirm || deleting) return;
    setDeleting(true);
    const { kind, ids } = deleteConfirm;
    const apiPath = kind === 'role'
      ? '/master/roles'
      : kind === 'industry'
        ? '/master/industries'
        : '/master/departments';
    const noun = kind === 'role' ? 'role' : kind === 'industry' ? 'industry' : 'department';
    try {
      const { ok, failed } = await softDeleteMany(apiPath, ids);
      if (kind === 'role') {
        roleClearRef.current?.();
        qc.invalidateQueries({ queryKey: ['v1', 'roles'] });
        qc.invalidateQueries({ queryKey: ['v1-master-roles'] });
        qc.invalidateQueries({ queryKey: ['v1-pipelines'] });
      } else if (kind === 'industry') {
        if (selectedIndustryId && ids.includes(selectedIndustryId)) {
          setSelectedIndustryId(null);
          setSelectedDepartmentId(null);
        }
        qc.invalidateQueries({ queryKey: ['v1', 'industries'] });
      } else {
        deptSectionRef.current?.clearSelection();
        if (selectedDepartmentId && ids.includes(selectedDepartmentId)) {
          setSelectedDepartmentId(null);
        }
        qc.invalidateQueries({ queryKey: ['v1', 'departments'] });
      }
      if (failed === 0) {
        const plural = noun === 'industry' ? 'industries' : `${noun}s`;
        toast.success(ok === 1 ? `${noun[0].toUpperCase()}${noun.slice(1)} deleted.` : `${ok} ${plural} deleted.`);
      } else {
        toast.error(`Deleted ${ok}, failed ${failed}.`);
      }
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const createIndustry = async () => {
    if (!industryForm.code.trim() || !industryForm.name.trim()) {
      return toast.error('Industry code and name are required.');
    }
    setIndustrySaving(true);
    try {
      await v1Post('/master/industries', {
        code: industryForm.code.trim().toUpperCase(),
        name: industryForm.name.trim(),
        status: 'active',
      });
      qc.invalidateQueries({ queryKey: ['v1', 'industries'] });
      toast.success(`Industry ${industryForm.name.trim()} created.`);
      setIndustryOpen(false);
      setIndustryForm({ code: '', name: '' });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not create industry.');
    } finally {
      setIndustrySaving(false);
    }
  };

  const level = selectedDepartmentId ? 'roles' : selectedIndustryId ? 'departments' : 'industries';

  return (
    <div className="flex h-[calc(100vh-3rem)] flex-col gap-4 md:h-[calc(100vh-4rem)]" data-testid="roles-departments-page">
      <PageHeader
        className="shrink-0"
        title="Roles & Departments"
        subtitle="Browse Industry → Department → Role. Pipelines auto-create 1:1 with each role."
      />

      <div className="flex shrink-0 flex-col gap-2" data-testid="hierarchy-nav">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              {level === 'industries' ? (
                <BreadcrumbPage>Industries</BreadcrumbPage>
              ) : (
                <BreadcrumbLink
                  href="#"
                  onClick={(e) => { e.preventDefault(); goToIndustries(); }}
                  data-testid="breadcrumb-industries"
                >
                  Industries
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {selectedIndustry ? (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  {level === 'departments' ? (
                    <BreadcrumbPage>{selectedIndustry.name}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink
                      href="#"
                      onClick={(e) => { e.preventDefault(); goToDepartments(); }}
                      data-testid="breadcrumb-industry"
                    >
                      {selectedIndustry.name}
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </>
            ) : null}
            {selectedDepartment ? (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{selectedDepartment.name}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            ) : null}
          </BreadcrumbList>
        </Breadcrumb>
        <p className="text-xs text-muted-foreground">
          {level === 'industries' && 'Select an industry to view its departments.'}
          {level === 'departments' && `Departments in ${selectedIndustry?.name || 'this industry'}. Select a department to view its roles.`}
          {level === 'roles' && `Roles in ${selectedDepartment?.name || 'this department'}.`}
        </p>
      </div>

      <Dialog open={industryOpen} onOpenChange={setIndustryOpen}>
        <DialogContent data-testid="add-industry-dialog">
          <DialogHeader><DialogTitle>New industry</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Code</label>
              <Input
                value={industryForm.code}
                onChange={(e) => setIndustryForm({ ...industryForm, code: e.target.value.toUpperCase() })}
                data-testid="industry-code-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Name</label>
              <Input value={industryForm.name} onChange={(e) => setIndustryForm({ ...industryForm, name: e.target.value })} data-testid="industry-name-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIndustryOpen(false)}>Cancel</Button>
            <Button onClick={createIndustry} disabled={industrySaving} data-testid="industry-create-confirm">
              {industrySaving ? 'Creating…' : 'Create industry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="min-h-0 flex-1">
        {level === 'industries' && (
          <IndustriesSection
            industries={industries}
            deptCountByIndustry={deptCountByIndustry}
            roleCountByIndustry={roleCountByIndustry}
            isLoading={industriesLoading}
            isError={industriesError}
            onSelectIndustry={handleSelectIndustry}
            onNewIndustry={() => setIndustryOpen(true)}
            onRequestDelete={setDeleteConfirm}
          />
        )}
        {level === 'departments' && (
          <DepartmentsSection
            ref={deptSectionRef}
            depts={filteredDepts}
            allDepts={depts}
            industries={industries}
            defaultIndustryId={selectedIndustryId || ''}
            roleCountByDept={roleCountByDept}
            contextLabel={selectedIndustry?.name || ''}
            isLoading={deptsLoading}
            isError={deptsError}
            onRequestDelete={setDeleteConfirm}
            onManageCategories={handleManageCategories}
            onSelectDepartment={handleSelectDepartment}
          />
        )}
        {level === 'roles' && (
          <RolesSection
            roles={filteredRoles}
            allRoles={roles}
            pipelines={pipelines}
            departments={industryScopedDepts}
            defaultDepartmentId={selectedDepartmentId || ''}
            contextLabel={selectedDepartment?.name || ''}
            isLoading={rolesLoading}
            isError={rolesError}
            onRequestDelete={setDeleteConfirm}
            onRegisterClearSelection={registerRoleClearSelection}
          />
        )}
      </div>

      <WorkCategoriesDialog
        dept={catOpen}
        initKey={catInitKey}
        onClose={() => setCatOpen(null)}
        onOpenDept={setCatOpen}
      />

      <AlertDialog open={!!deleteConfirm} onOpenChange={(v) => { if (!deleting && !v) setDeleteConfirm(null); }}>
        <AlertDialogContent data-testid="master-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(() => {
                const kind = deleteConfirm?.kind;
                const count = deleteConfirm?.ids?.length || 0;
                const plural = kind === 'role' ? 'roles' : kind === 'industry' ? 'industries' : 'departments';
                const singular = kind === 'role' ? 'role' : kind === 'industry' ? 'industry' : 'department';
                return count > 1
                  ? `Delete ${count} ${plural}?`
                  : `Delete this ${singular}?`;
              })()}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm?.label}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="master-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="master-delete-confirm-action"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
